import { formatDateInputValue } from '@/lib/format-utils';
import type { BetweenDate, RelativeDate } from '@reach/shared-types/conditions';
import React from 'react';
import { RelativeDateBuilder } from './RelativeDateBuilder';
import styles from './SegmentVisualBuilder.module.css';
import { isRelativeDate } from './ValueInput/date-value-utils';

/**
 * Sentinel year for month-day-only values; backend ignores year for any-year operators.
 * Use a leap year (2000) so February has 29 days and Feb 29 is a valid stored date.
 */
const MONTH_DAY_SENTINEL_YEAR = 2000;

function getDaysInMonth(month: number, year: number): number {
  return new Date(year, month, 0).getDate();
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

interface BetweenDateBuilderProps {
  value: BetweenDate | null;
  onChange: (value: BetweenDate) => void;
  disabled?: boolean;
  /**
   * 'relative' = days ago/from now.
   * 'absolute' = date picker.
   */
  dateValueMode: 'relative' | 'absolute';
  /** When true and dateValueMode is absolute, show month/day only (no year) for "any year" operators. */
  monthDayOnly?: boolean;
}

/**
 * UI for "between" date operators: start and end.
 * - between_month_day: relative (days ago/from now).
 * - between_month_day_year: absolute date picker.
 */
export const BetweenDateBuilder: React.FC<BetweenDateBuilderProps> = ({
  value,
  onChange,
  disabled = false,
  dateValueMode,
  monthDayOnly = false,
}) => {
  if (dateValueMode === 'absolute') {
    const startDate = value?.startDate instanceof Date ? value.startDate : null;
    const endDate = value?.endDate instanceof Date ? value.endDate : null;

    if (monthDayOnly) {
      const startMonth = startDate ? startDate.getMonth() + 1 : 0;
      const startDay = startDate ? startDate.getDate() : 0;
      const endMonth = endDate ? endDate.getMonth() + 1 : 0;
      const endDay = endDate ? endDate.getDate() : 0;
      const startDaysInMonth = startMonth ? getDaysInMonth(startMonth, MONTH_DAY_SENTINEL_YEAR) : 31;
      const endDaysInMonth = endMonth ? getDaysInMonth(endMonth, MONTH_DAY_SENTINEL_YEAR) : 31;

      const toDate = (month: number, day: number) =>
        new Date(MONTH_DAY_SENTINEL_YEAR, month - 1, day, 12, 0, 0);

      const handleStartMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const m = Number(e.target.value);
        const cappedDay = Math.min(startDay || 1, getDaysInMonth(m, MONTH_DAY_SENTINEL_YEAR));
        onChange({
          startDate: toDate(m, cappedDay),
          endDate: endDate ?? toDate(m, cappedDay),
        });
      };
      const handleStartDayChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const day = Number(e.target.value);
        const m = startMonth || 1;
        onChange({
          startDate: toDate(m, day),
          endDate: endDate ?? toDate(m, day),
        });
      };
      const handleEndMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const m = Number(e.target.value);
        const cappedDay = Math.min(endDay || 1, getDaysInMonth(m, MONTH_DAY_SENTINEL_YEAR));
        onChange({
          startDate: startDate ?? toDate(m, cappedDay),
          endDate: toDate(m, cappedDay),
        });
      };
      const handleEndDayChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const day = Number(e.target.value);
        const m = endMonth || 1;
        onChange({
          startDate: startDate ?? toDate(m, day),
          endDate: toDate(m, day),
        });
      };

      return (
        <div className={styles.betweenDateBuilder}>
          <div className={styles.betweenDateRow}>
            <span className={styles.betweenDateLabel}>Start:</span>
            <div className={styles.monthDayInputs}>
              <select
                className={styles.selectInput}
                value={startMonth || ''}
                onChange={handleStartMonthChange}
                disabled={disabled}
                aria-label="Start month">
                <option value="">Month</option>
                {MONTH_NAMES.map((name, i) => (
                  <option key={name} value={i + 1}>
                    {name}
                  </option>
                ))}
              </select>
              <select
                className={styles.selectInput}
                value={startDay || ''}
                onChange={handleStartDayChange}
                disabled={disabled}
                aria-label="Start day">
                <option value="">Day</option>
                {Array.from({ length: startDaysInMonth }, (_, i) => i + 1).map(d => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className={styles.betweenDateRow}>
            <span className={styles.betweenDateLabel}>End:</span>
            <div className={styles.monthDayInputs}>
              <select
                className={styles.selectInput}
                value={endMonth || ''}
                onChange={handleEndMonthChange}
                disabled={disabled}
                aria-label="End month">
                <option value="">Month</option>
                {MONTH_NAMES.map((name, i) => (
                  <option key={name} value={i + 1}>
                    {name}
                  </option>
                ))}
              </select>
              <select
                className={styles.selectInput}
                value={endDay || ''}
                onChange={handleEndDayChange}
                disabled={disabled}
                aria-label="End day">
                <option value="">Day</option>
                {Array.from({ length: endDaysInMonth }, (_, i) => i + 1).map(d => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      );
    }

    const startStr = startDate ? formatDateInputValue(startDate) : '';
    const endStr = endDate ? formatDateInputValue(endDate) : '';

    const handleStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      if (!v) return;
      const d = new Date(v + 'T12:00:00');
      onChange({
        startDate: d,
        endDate: endDate ?? d,
      });
    };

    const handleEndChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      if (!v) return;
      const d = new Date(v + 'T12:00:00');
      onChange({
        startDate: startDate ?? d,
        endDate: d,
      });
    };

    return (
      <div className={styles.betweenDateBuilder}>
        <div className={styles.betweenDateRow}>
          <span className={styles.betweenDateLabel}>Start:</span>
          <input
            type="date"
            className={styles.textInput}
            value={startStr}
            onChange={handleStartChange}
            disabled={disabled}
            aria-label="Start date"
          />
        </div>
        <div className={styles.betweenDateRow}>
          <span className={styles.betweenDateLabel}>End:</span>
          <input
            type="date"
            className={styles.textInput}
            value={endStr}
            onChange={handleEndChange}
            disabled={disabled}
            aria-label="End date"
          />
        </div>
      </div>
    );
  }

  const startRelative: RelativeDate | null = value?.startDate
    ? isRelativeDate(value.startDate)
      ? value.startDate
      : null
    : null;
  const endRelative: RelativeDate | null = value?.endDate
    ? isRelativeDate(value.endDate)
      ? value.endDate
      : null
    : null;

  const handleStartChange = (rel: RelativeDate) => {
    onChange({
      startDate: rel,
      endDate: endRelative ?? rel,
    });
  };

  const handleEndChange = (rel: RelativeDate) => {
    onChange({
      startDate: startRelative ?? rel,
      endDate: rel,
    });
  };

  return (
    <div className={styles.betweenDateBuilder}>
      <div className={styles.betweenDateRow}>
        <span className={styles.betweenDateLabel}>Start:</span>
        <RelativeDateBuilder value={startRelative} onChange={handleStartChange} disabled={disabled} />
      </div>
      <div className={styles.betweenDateRow}>
        <span className={styles.betweenDateLabel}>End:</span>
        <RelativeDateBuilder value={endRelative} onChange={handleEndChange} disabled={disabled} />
      </div>
    </div>
  );
};
